import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Calendar } from "../components/ui/calendar";
import { useCurrency, BASE_EXAM_FEE_EUR, PREMIUM_CERT_FEE_EUR } from "../context/CurrencyContext";
import { addDays, isBefore, startOfDay, parseISO, differenceInHours } from "date-fns";
import { 
  User, 
  LogOut, 
  FileText, 
  Upload, 
  Award, 
  CreditCard, 
  HelpCircle,
  CheckCircle,
  Clock,
  AlertCircle,
  Download,
  ExternalLink,
  Edit2,
  Save,
  X,
  CalendarIcon,
  MapPin,
  Phone,
  Globe,
  Video,
  Info,
  GraduationCap,
  ChevronRight,
  Loader2
} from "lucide-react";

const INSTRUMENTS = [
  "Piano",
  "Guitar", 
  "Violin",
  "Drums",
  "Vocals - Hindustani Classical",
  "Vocals - Indian Contemporary",
  "Vocals - Western Classical",
  "Vocals - English Contemporary",
  "Alto Saxophone",
  "Voice Over",
  "Music Therapy"
];

const EXAM_LEVELS = [
  { value: "1", label: "Level 1" },
  { value: "2", label: "Level 2" },
  { value: "3", label: "Level 3" },
  { value: "4", label: "Level 4" },
  { value: "5", label: "Level 5" },
  { value: "6", label: "Level 6" },
  { value: "ai-voiceover", label: "AI Voiceover Master Certification" },
  { value: "music-therapy", label: "Music Therapy Professional Certification" }
];

// Generate time slots from 7am to 11pm (1 hour apart)
const TIME_SLOTS = Array.from({ length: 17 }, (_, i) => {
  const hour = i + 7;
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour > 12 ? hour - 12 : hour;
  return {
    value: `${hour}:00`,
    label: `${displayHour}:00 ${period}`
  };
});

// List of countries for profile edit
const COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Argentina", "Australia", "Austria", "Bangladesh", 
  "Belgium", "Brazil", "Canada", "China", "Colombia", "Denmark", "Egypt", "Finland", 
  "France", "Germany", "Greece", "Hong Kong", "India", "Indonesia", "Iran", "Iraq", 
  "Ireland", "Israel", "Italy", "Japan", "Kenya", "Kuwait", "Malaysia", "Mexico", 
  "Morocco", "Nepal", "Netherlands", "New Zealand", "Nigeria", "Norway", "Pakistan", 
  "Philippines", "Poland", "Portugal", "Qatar", "Romania", "Russia", "Saudi Arabia", 
  "Singapore", "South Africa", "South Korea", "Spain", "Sri Lanka", "Sweden", 
  "Switzerland", "Taiwan", "Thailand", "Turkey", "UAE", "Uganda", "Ukraine", 
  "United Kingdom", "United States", "Vietnam", "Other"
];

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { formatPrice } = useCurrency();
  const [user, setUser] = useState(location.state?.user || null);
  const [loading, setLoading] = useState(!location.state?.user);
  const [registrations, setRegistrations] = useState([]);
  const [availableLevels, setAvailableLevels] = useState({});
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registrationStep, setRegistrationStep] = useState(1); // 1: select, 2: date/time, 3: payment
  const [selectedInstrument, setSelectedInstrument] = useState("");
  const [selectedLevel, setSelectedLevel] = useState("");
  const [selectedExamType, setSelectedExamType] = useState("live");
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [registering, setRegistering] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [activeRegistration, setActiveRegistration] = useState(null);
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [profileData, setProfileData] = useState({ name: "", email: "", phone: "", country: "", city: "" });
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState(null);
  const [rescheduleTime, setRescheduleTime] = useState("");
  const [rescheduling, setRescheduling] = useState(false);
  const [scholarshipStatus, setScholarshipStatus] = useState(null); // null | "draft" | "submitted" | "paid"
  const [scholarshipLoading, setScholarshipLoading] = useState(true);
  const [scholarshipAppId, setScholarshipAppId] = useState("");
  const [scholarshipPaying, setScholarshipPaying] = useState(false);
  const [forexRate, setForexRate] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Calculate the minimum selectable date (2 weeks from today)
  const minSelectableDate = addDays(startOfDay(new Date()), 14);

  // Function to check if a date is disabled (within 2 weeks)
  const isDateDisabled = (date) => {
    return isBefore(date, minSelectableDate);
  };

  // Get exam fee based on selected level
  const getExamFee = () => {
    if (selectedLevel === "ai-voiceover" || selectedLevel === "music-therapy") {
      return PREMIUM_CERT_FEE_EUR;
    }
    return BASE_EXAM_FEE_EUR;
  };

  // Check if meeting link should be visible (24 hours before exam to 48 hours after exam start)
  const isMeetingLinkVisible = (registration) => {
    if (!registration || !registration.exam_date || !registration.exam_time || !registration.meeting_link_student) {
      return false;
    }
    
    try {
      // Parse exam date and time
      const examDateStr = registration.exam_date;
      const examTimeStr = registration.exam_time; // Format: "HH:00"
      const examHour = parseInt(examTimeStr.split(':')[0], 10);
      
      // Create exam datetime
      const examDate = parseISO(examDateStr);
      examDate.setHours(examHour, 0, 0, 0);
      
      const now = new Date();
      const hoursUntilExam = differenceInHours(examDate, now);
      const hoursAfterExam = differenceInHours(now, examDate);
      
      // Show link 24 hours before exam and up to 48 hours after exam start
      return hoursUntilExam <= 24 && hoursAfterExam <= 48;
    } catch (error) {
      console.error("Error checking meeting link visibility:", error);
      return false;
    }
  };

  // Get meeting link status message
  const getMeetingLinkStatus = (registration) => {
    if (!registration || !registration.exam_date || !registration.exam_time) {
      return { visible: false, message: "" };
    }
    
    try {
      const examDateStr = registration.exam_date;
      const examTimeStr = registration.exam_time;
      const examHour = parseInt(examTimeStr.split(':')[0], 10);
      
      const examDate = parseISO(examDateStr);
      examDate.setHours(examHour, 0, 0, 0);
      
      const now = new Date();
      const hoursUntilExam = differenceInHours(examDate, now);
      
      if (hoursUntilExam > 24) {
        const daysUntil = Math.floor(hoursUntilExam / 24);
        return {
          visible: false,
          message: `Your video meeting link will appear here ${daysUntil > 1 ? `in ${daysUntil} days` : 'tomorrow'} (24 hours before your exam).`
        };
      }
      
      return { visible: true, message: "" };
    } catch (error) {
      return { visible: false, message: "Unable to determine exam time." };
    }
  };

  // Check if reschedule is allowed (more than 72 hours before exam and not already rescheduled)
  const canReschedule = (registration) => {
    if (!registration || !registration.exam_date || !registration.exam_time) {
      return { allowed: false, reason: "Exam date/time not set" };
    }
    
    // Check if already rescheduled
    if (registration.reschedule_count >= 1) {
      return { allowed: false, reason: "You have already rescheduled this exam. Rescheduling is allowed only once." };
    }
    
    // Check if exam is completed or result declared
    if (registration.status === "result_declared" || registration.status === "exam_completed") {
      return { allowed: false, reason: "Cannot reschedule a completed exam." };
    }
    
    try {
      const examDateStr = registration.exam_date;
      const examTimeStr = registration.exam_time;
      const examHour = parseInt(examTimeStr.split(':')[0], 10);
      
      const examDate = parseISO(examDateStr);
      examDate.setHours(examHour, 0, 0, 0);
      
      const now = new Date();
      const hoursUntilExam = differenceInHours(examDate, now);
      
      if (hoursUntilExam < 72) {
        return { allowed: false, reason: "Cannot reschedule within 72 hours of the exam." };
      }
      
      return { allowed: true, reason: "" };
    } catch (error) {
      return { allowed: false, reason: "Unable to determine exam time." };
    }
  };

  // Handle exam reschedule
  const handleRescheduleExam = async () => {
    if (!rescheduleDate || !rescheduleTime || !activeRegistration) return;
    
    setRescheduling(true);
    try {
      const newExamDateStr = rescheduleDate.toISOString().split('T')[0];
      
      const response = await fetch(`${API_URL}/api/exams/reschedule`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          registration_id: activeRegistration.registration_id,
          new_exam_date: newExamDateStr,
          new_exam_time: rescheduleTime,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Reschedule failed");
      }

      alert("Exam rescheduled successfully! You will receive a confirmation email shortly.");
      setShowRescheduleModal(false);
      setRescheduleDate(null);
      setRescheduleTime("");
      fetchData();
    } catch (error) {
      alert(error.message);
    } finally {
      setRescheduling(false);
    }
  };

  // Handle scholarship application fee payment
  const handleScholarshipPayment = async () => {
    setScholarshipPaying(true);
    try {
      const feeEUR = 100;
      const feeINR = getINRAmount(feeEUR);
      const amountInPaise = Math.round(feeINR * 100);

      const orderRes = await fetch(`${API_URL}/api/student-scholarship/create-payment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ amount_inr: amountInPaise }),
      });
      if (!orderRes.ok) {
        const err = await orderRes.json();
        throw new Error(err.detail || "Failed to create payment order");
      }
      const orderData = await orderRes.json();

      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "Global Music Conservatory",
        description: "Scholarship Application Fee (EUR 100)",
        order_id: orderData.order_id,
        prefill: { name: user?.name, email: user?.email, contact: user?.phone },
        theme: { color: "#182033" },
        handler: async function (response) {
          try {
            const verifyRes = await fetch(`${API_URL}/api/student-scholarship/verify-payment`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              }),
            });
            if (!verifyRes.ok) {
              const err = await verifyRes.json();
              throw new Error(err.detail || "Payment verification failed");
            }
            setScholarshipStatus("paid");
            alert("Payment successful! Your scholarship application is now complete. You will receive a confirmation email.");
          } catch (error) {
            alert("Payment verification failed: " + error.message);
          } finally {
            setScholarshipPaying(false);
          }
        },
        modal: { ondismiss: () => setScholarshipPaying(false) },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on("payment.failed", (response) => {
        alert("Payment failed: " + response.error.description);
        setScholarshipPaying(false);
      });
      razorpay.open();
    } catch (error) {
      alert("Error: " + error.message);
      setScholarshipPaying(false);
    }
  };

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setProfileData({
        name: location.state.user.name || "",
        email: location.state.user.email || "",
        phone: location.state.user.phone || "",
        country: location.state.user.country || "",
        city: location.state.user.city || "",
      });
      setLoading(false);
      fetchData();
      return;
    }

    // Verify auth via /auth/me
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/me`, {
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error("Not authenticated");
        }

        const userData = await response.json();
        setUser(userData);
        setProfileData({
          name: userData.name || "",
          email: userData.email || "",
          phone: userData.phone || "",
          country: userData.country || "",
          city: userData.city || "",
        });
        fetchData();
      } catch (error) {
        navigate("/login");
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [navigate, location.state, API_URL]);

  const fetchData = async () => {
    try {
      // Fetch registrations
      const regResponse = await fetch(`${API_URL}/api/exams/my-registrations`, {
        credentials: "include",
      });
      if (regResponse.ok) {
        const regData = await regResponse.json();
        setRegistrations(regData.registrations || []);
        
        // Set active registration (most recent non-completed)
        const active = regData.registrations?.find(r => 
          !["result_declared"].includes(r.status) || 
          (r.status === "result_declared" && r.result !== "pass" && r.result !== "merit")
        );
        setActiveRegistration(active || regData.registrations?.[0] || null);
      }

      // Fetch available levels
      const levelResponse = await fetch(`${API_URL}/api/exams/user-levels`, {
        credentials: "include",
      });
      if (levelResponse.ok) {
        const levelData = await levelResponse.json();
        setAvailableLevels(levelData.available_levels || {});
      }

      // Fetch scholarship application status
      try {
        const schRes = await fetch(`${API_URL}/api/student-scholarship/retrieve`, { credentials: "include" });
        if (schRes.ok) {
          const schData = await schRes.json();
          if (schData.found && schData.application) {
            setScholarshipStatus(schData.application.status);
            setScholarshipAppId(schData.application.application_id || "");
          }
        }
      } catch (e) { console.error("Scholarship fetch error:", e); }
      finally { setScholarshipLoading(false); }

      // Fetch live forex rate
      try {
        const fxRes = await fetch(`${API_URL}/api/forex/eur-inr`);
        if (fxRes.ok) {
          const fxData = await fxRes.json();
          if (fxData.rate) setForexRate(fxData.rate);
        }
      } catch (e) { console.error("Forex fetch error:", e); }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    navigate("/login");
  };

  // Live EUR to INR conversion (fetched from forex API, fallback to 90)
  const getINRAmount = (eurAmount) => {
    const rate = forexRate || 90;
    return Math.ceil(eurAmount * rate);
  };

  const handleRazorpayPayment = async () => {
    if (!selectedInstrument || !selectedLevel || !selectedDate || !selectedTime) return;
    
    setRegistering(true);
    try {
      const examDateStr = selectedDate.toISOString().split('T')[0];
      const feeEUR = getExamFee();
      const feeINR = getINRAmount(feeEUR);
      const amountInPaise = feeINR * 100; // Razorpay expects amount in paise
      
      // Get user's timezone
      const studentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Create Razorpay order
      const orderResponse = await fetch(`${API_URL}/api/payments/create-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          instrument: selectedInstrument,
          level: selectedLevel,
          exam_date: examDateStr,
          exam_time: selectedTime,
          amount_inr: amountInPaise,
          student_timezone: studentTimezone,
        }),
      });

      if (!orderResponse.ok) {
        const errorData = await orderResponse.json();
        throw new Error(errorData.detail || "Failed to create payment order");
      }

      const orderData = await orderResponse.json();

      // Initialize Razorpay checkout
      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "Global Music Conservatory",
        description: `Exam Fee: ${selectedInstrument} - ${selectedLevel === "ai-voiceover" ? "AI Voiceover Master Certification" : selectedLevel === "music-therapy" ? "Music Therapy Professional Certification" : `Level ${selectedLevel}`}`,
        order_id: orderData.order_id,
        prefill: {
          name: orderData.user_name,
          email: orderData.user_email,
          contact: orderData.user_phone,
        },
        theme: {
          color: "#182033",
        },
        handler: async function (response) {
          // Payment successful - verify and complete registration
          try {
            const verifyResponse = await fetch(`${API_URL}/api/payments/verify`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                instrument: selectedInstrument,
                level: selectedLevel,
                exam_date: examDateStr,
                exam_time: selectedTime,
                student_timezone: studentTimezone,
              }),
            });

            if (!verifyResponse.ok) {
              const errorData = await verifyResponse.json();
              throw new Error(errorData.detail || "Payment verification failed");
            }

            const result = await verifyResponse.json();
            alert("Payment successful! Your exam registration is complete. You will receive a confirmation email shortly.");
            
            // Reset form and refresh data
            setShowRegisterModal(false);
            setSelectedInstrument("");
            setSelectedLevel("");
            setSelectedDate(null);
            setSelectedTime("");
            setRegistrationStep(1);
            fetchData();
          } catch (error) {
            alert("Payment verification failed: " + error.message);
          }
        },
        modal: {
          ondismiss: function () {
            setRegistering(false);
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on("payment.failed", function (response) {
        alert("Payment failed: " + response.error.description);
        setRegistering(false);
      });
      
      razorpay.open();
    } catch (error) {
      alert("Error: " + error.message);
      setRegistering(false);
    }
  };

  const handleRegisterExam = async () => {
    if (!selectedInstrument || !selectedLevel || !selectedDate || !selectedTime) return;
    
    setRegistering(true);
    try {
      // Format the date as ISO string
      const examDateStr = selectedDate.toISOString().split('T')[0];
      
      const response = await fetch(`${API_URL}/api/exams/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          instrument: selectedInstrument,
          level: parseInt(selectedLevel) || selectedLevel,
          exam_type: selectedExamType,
          exam_date: examDateStr,
          exam_time: selectedTime,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Registration failed");
      }

      setShowRegisterModal(false);
      setSelectedInstrument("");
      setSelectedLevel("");
      setSelectedDate(null);
      setSelectedTime("");
      setRegistrationStep(1);
      fetchData();
    } catch (error) {
      alert(error.message);
    } finally {
      setRegistering(false);
    }
  };

  const handleVideoUpload = async (e, registrationId) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingVideo(true);
    try {
      const formData = new FormData();
      formData.append("video", file);

      const response = await fetch(`${API_URL}/api/exams/${registrationId}/upload-video`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Upload failed");
      }

      fetchData();
    } catch (error) {
      alert(error.message);
    } finally {
      setUploadingVideo(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      registered: { label: "Registered", color: "bg-blue-100 text-blue-800", icon: Clock },
      video_submitted: { label: "Submitted", color: "bg-amber-100 text-amber-800", icon: CheckCircle },
      under_evaluation: { label: "Under Evaluation", color: "bg-purple-100 text-purple-800", icon: Clock },
      result_declared: { label: "Result Declared", color: "bg-green-100 text-green-800", icon: Award },
    };
    const badge = badges[status] || badges.registered;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="h-3 w-3" />
        {badge.label}
      </span>
    );
  };

  const getPrimaryAction = () => {
    if (!activeRegistration) {
      return { label: "Register for Exam", action: () => setShowRegisterModal(true), icon: FileText };
    }

    switch (activeRegistration.status) {
      case "registered":
        if (activeRegistration.exam_type === "video") {
          return { label: "Upload Performance", action: null, isUpload: true, icon: Upload };
        }
        return { label: "View Exam Details", action: () => {}, icon: FileText };
      case "video_submitted":
        return { label: "Awaiting Evaluation", action: null, disabled: true, icon: Clock };
      case "under_evaluation":
        return { label: "Under Evaluation", action: null, disabled: true, icon: Clock };
      case "result_declared":
        if (activeRegistration.result === "pass" || activeRegistration.result === "merit") {
          return { label: "Download Certificate", action: () => {}, icon: Download };
        }
        return { label: "Register for Retry", action: () => setShowRegisterModal(true), icon: FileText };
      default:
        return { label: "Register for Exam", action: () => setShowRegisterModal(true), icon: FileText };
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#8c7648] border-r-transparent"></div>
          <p className="mt-4 text-sm text-[#6b6a66]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const primaryAction = getPrimaryAction();

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <section className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 rounded-2xl border border-[#e1d2b3] bg-[#fdf8ef] p-5 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#1b2236] text-[#f8f2e6]">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="h-14 w-14 rounded-full object-cover" />
            ) : (
              <User className="h-6 w-6" />
            )}
          </div>
          <div>
            <h1 className="text-lg font-semibold text-[#182033]">{user?.name || "Student"}</h1>
            <p className="text-sm text-[#6b6a66]">{user?.email}</p>
            {activeRegistration && (
              <div className="mt-1 flex items-center gap-2">
                <span className="text-xs font-medium text-[#8c7648]">
                  {activeRegistration.instrument} • Level {activeRegistration.level}
                </span>
                {getStatusBadge(activeRegistration.status)}
              </div>
            )}
          </div>
        </div>
        <Button
          variant="outline"
          onClick={handleLogout}
          className="border-[#d5c39d] bg-white text-[#182033] hover:bg-[#f5ebd9]"
        >
          <LogOut className="mr-2 h-4 w-4" />
          Sign Out
        </Button>
      </section>

      {/* Student Profile Section */}
      <section className="rounded-2xl border border-[#e1d2b3] bg-[#fdf8ef] p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-[#182033] flex items-center gap-2">
            <User className="h-4 w-4" />
            Student Profile
          </h3>
          {!showProfileEdit ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowProfileEdit(true)}
              className="text-xs text-[#8c7648] hover:text-[#6b5a38]"
            >
              <Edit2 className="h-3 w-3 mr-1" />
              Edit
            </Button>
          ) : (
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowProfileEdit(false)}
                className="text-xs text-[#6b6a66]"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
        
        {!showProfileEdit ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <p className="text-xs text-[#6b6a66] mb-0.5 flex items-center gap-1"><User className="h-3 w-3" /> Full Name</p>
              <p className="text-sm font-medium text-[#182033]">{user?.name || "—"}</p>
            </div>
            <div>
              <p className="text-xs text-[#6b6a66] mb-0.5">Email Address</p>
              <p className="text-sm font-medium text-[#182033]">{user?.email || "—"}</p>
            </div>
            <div>
              <p className="text-xs text-[#6b6a66] mb-0.5 flex items-center gap-1"><Phone className="h-3 w-3" /> Phone Number</p>
              <p className="text-sm font-medium text-[#182033]">{user?.phone || "Not provided"}</p>
            </div>
            <div>
              <p className="text-xs text-[#6b6a66] mb-0.5 flex items-center gap-1"><Globe className="h-3 w-3" /> Country</p>
              <p className="text-sm font-medium text-[#182033]">{user?.country || "Not provided"}</p>
            </div>
            <div>
              <p className="text-xs text-[#6b6a66] mb-0.5 flex items-center gap-1"><MapPin className="h-3 w-3" /> City</p>
              <p className="text-sm font-medium text-[#182033]">{user?.city || "Not provided"}</p>
            </div>
            <div>
              <p className="text-xs text-[#6b6a66] mb-0.5">Account Type</p>
              <p className="text-sm font-medium text-[#182033] capitalize">{user?.auth_type === "google" ? "Google Account" : "Email Account"}</p>
            </div>
            <div>
              <p className="text-xs text-[#6b6a66] mb-0.5">Member Since</p>
              <p className="text-sm font-medium text-[#182033]">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}
              </p>
            </div>
            <div>
              <p className="text-xs text-[#6b6a66] mb-0.5">Total Certifications</p>
              <p className="text-sm font-medium text-[#182033]">
                {registrations.filter(r => r.result === "pass" || r.result === "merit").length}
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label className="text-xs text-[#6b6a66]">Full Name</Label>
                <Input
                  value={profileData.name}
                  onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                  className="border-[#d5c39d] bg-white text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-[#6b6a66]">Phone Number</Label>
                <Input
                  value={profileData.phone}
                  onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                  placeholder="Enter phone number"
                  className="border-[#d5c39d] bg-white text-sm"
                />
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label className="text-xs text-[#6b6a66]">Country</Label>
                <Select 
                  value={profileData.country} 
                  onValueChange={(value) => setProfileData({ ...profileData, country: value })}
                >
                  <SelectTrigger className="border-[#d5c39d] bg-white text-sm">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {COUNTRIES.map((country) => (
                      <SelectItem key={country} value={country}>{country}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-[#6b6a66]">City</Label>
                <Input
                  value={profileData.city}
                  onChange={(e) => setProfileData({ ...profileData, city: e.target.value })}
                  placeholder="Enter city"
                  className="border-[#d5c39d] bg-white text-sm"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowProfileEdit(false)}
                className="border-[#d5c39d] text-xs"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={async () => {
                  try {
                    const response = await fetch(`${API_URL}/api/auth/profile`, {
                      method: "PUT",
                      headers: { "Content-Type": "application/json" },
                      credentials: "include",
                      body: JSON.stringify({
                        name: profileData.name,
                        phone: profileData.phone,
                        country: profileData.country,
                        city: profileData.city
                      }),
                    });
                    if (response.ok) {
                      const data = await response.json();
                      setUser(data.user);
                    }
                  } catch (error) {
                    console.error("Profile update error:", error);
                  }
                  setShowProfileEdit(false);
                }}
                className="bg-[#1b2236] text-[#f8f2e6] text-xs"
              >
                <Save className="h-3 w-3 mr-1" />
                Save Changes
              </Button>
            </div>
          </div>
        )}
      </section>

      {/* Primary Action Area */}
      <section className="rounded-2xl border border-[#ddcaa4] bg-gradient-to-br from-[#1b2236] to-[#2a3654] p-6 text-center shadow-lg">
        <h2 className="mb-4 text-lg font-semibold text-[#f8f2e6]">
          {activeRegistration 
            ? `${activeRegistration.instrument} - Level ${activeRegistration.level} Certification`
            : "Start Your Music Certification Journey"
          }
        </h2>
        
        {primaryAction.isUpload ? (
          <label className="cursor-pointer">
            <input
              type="file"
              accept="video/*"
              className="hidden"
              onChange={(e) => handleVideoUpload(e, activeRegistration?.registration_id)}
              disabled={uploadingVideo}
            />
            <span className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-8 py-3 text-sm font-semibold uppercase tracking-wider text-[#1b2236] shadow-lg transition hover:bg-amber-300">
              {uploadingVideo ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-[#1b2236] border-r-transparent"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <primaryAction.icon className="h-5 w-5" />
                  {primaryAction.label}
                </>
              )}
            </span>
          </label>
        ) : (
          <Button
            onClick={primaryAction.action}
            disabled={primaryAction.disabled}
            className="rounded-full bg-amber-400 px-8 py-3 text-sm font-semibold uppercase tracking-wider text-[#1b2236] shadow-lg transition hover:bg-amber-300 disabled:opacity-50"
          >
            <primaryAction.icon className="mr-2 h-5 w-5" />
            {primaryAction.label}
          </Button>
        )}
      </section>

      {/* Exam Details Section */}
      {activeRegistration && (
        <section className="grid gap-4 md:grid-cols-2">
          {/* Exam Info Card */}
          <Card className="border-[#e1d2b3] bg-[#fdf8ef]">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm font-semibold text-[#182033]">
                <FileText className="h-4 w-4" />
                Exam Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[#6b6a66]">Instrument</span>
                <span className="font-medium text-[#182033]">{activeRegistration.instrument}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#6b6a66]">Level</span>
                <span className="font-medium text-[#182033]">Level {activeRegistration.level}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#6b6a66]">Exam Type</span>
                <span className="font-medium text-[#182033] capitalize">{activeRegistration.exam_type}</span>
              </div>
              {activeRegistration.exam_type === "video" && activeRegistration.submission_deadline && (
                <div className="flex justify-between">
                  <span className="text-[#6b6a66]">Submission Deadline</span>
                  <span className="font-medium text-[#182033]">
                    {new Date(activeRegistration.submission_deadline).toLocaleDateString()}
                  </span>
                </div>
              )}
              {activeRegistration.exam_type === "live" && activeRegistration.exam_date && (
                <div className="flex justify-between">
                  <span className="text-[#6b6a66]">Exam Date</span>
                  <span className="font-medium text-[#182033]">
                    {new Date(activeRegistration.exam_date).toLocaleDateString()}
                  </span>
                </div>
              )}
              {activeRegistration.exam_time && (
                <div className="flex justify-between">
                  <span className="text-[#6b6a66]">Exam Time</span>
                  <span className="font-medium text-[#182033]">
                    {(() => {
                      const hour = parseInt(activeRegistration.exam_time.split(':')[0], 10);
                      const period = hour >= 12 ? 'PM' : 'AM';
                      const displayHour = hour > 12 ? hour - 12 : hour;
                      const timezone = activeRegistration.student_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
                      return `${displayHour}:00 ${period} (${timezone})`;
                    })()}
                  </span>
                </div>
              )}
              {activeRegistration.reschedule_count > 0 && (
                <div className="flex justify-between pt-2 border-t border-[#e1d2b3]">
                  <span className="text-[#6b6a66]">Rescheduled</span>
                  <span className="font-medium text-amber-600">Yes (1 of 1 used)</span>
                </div>
              )}
              
              {/* Reschedule Button */}
              {activeRegistration.status !== "result_declared" && activeRegistration.status !== "exam_completed" && (
                <div className="pt-3 border-t border-[#e1d2b3]">
                  {canReschedule(activeRegistration).allowed ? (
                    <Button
                      onClick={() => setShowRescheduleModal(true)}
                      variant="outline"
                      size="sm"
                      className="w-full border-[#d5c39d] text-[#8c7648] hover:bg-[#fdf8ef]"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      Reschedule Exam
                    </Button>
                  ) : (
                    <div className="text-xs text-[#6b6a66] text-center p-2 bg-slate-50 rounded">
                      {canReschedule(activeRegistration).reason}
                    </div>
                  )}
                  <p className="text-xs text-[#8c7648] text-center mt-2">
                    Note: Rescheduling is allowed only once, and only up to 72 hours before the exam.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Live Exam Video Meeting Card */}
          {activeRegistration.exam_date && activeRegistration.status !== "result_declared" && activeRegistration.status !== "exam_completed" && (
            <Card className="border-[#1e40af] bg-gradient-to-br from-[#1e3a8a] to-[#1e40af] md:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm font-semibold text-white">
                  <Video className="h-4 w-4" />
                  Live Exam Video Meeting
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {isMeetingLinkVisible(activeRegistration) ? (
                  <>
                    <div className="rounded-lg bg-white/10 p-4">
                      <p className="text-sm text-blue-100 mb-3">
                        Your exam video meeting link is now active. Click the button below to join your Live Examination at the scheduled time.
                      </p>
                      <a
                        href={activeRegistration.meeting_link_student}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-lg bg-green-500 px-6 py-3 text-sm font-semibold text-white transition hover:bg-green-600"
                      >
                        <Video className="h-5 w-5" />
                        Join Live Exam
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </div>
                    <div className="rounded-lg bg-white/5 p-3 text-xs text-blue-200">
                      <p className="font-semibold text-white mb-2 flex items-center gap-1">
                        <Info className="h-3 w-3" />
                        Important Instructions:
                      </p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>Join the meeting 10 minutes before your scheduled time</li>
                        <li>You will be placed in a waiting room until the examiner admits you</li>
                        <li>Ensure your camera and microphone are working</li>
                        <li>Have your instrument ready and tuned</li>
                        <li>Keep required music sheets ready</li>
                        <li>The examiner will end the meeting when your exam is complete</li>
                      </ul>
                    </div>
                  </>
                ) : (
                  <div className="rounded-lg bg-white/10 p-4">
                    <div className="flex items-start gap-3">
                      <Clock className="h-5 w-5 text-blue-300 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-white mb-1">Video Link Not Yet Available</p>
                        <p className="text-sm text-blue-200">
                          {getMeetingLinkStatus(activeRegistration).message}
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-white/20">
                      <p className="text-xs text-blue-300 font-medium mb-2">What to expect:</p>
                      <ul className="text-xs text-blue-200 space-y-1 list-disc list-inside">
                        <li>A video meeting link will appear here 24 hours before your exam</li>
                        <li>The link will be valid for 48 hours from the time it appears</li>
                        <li>Click the link at your scheduled exam time to join</li>
                        <li>Wait in the lobby until the examiner admits you</li>
                      </ul>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Result Card (if available) */}
          {activeRegistration.status === "result_declared" && (
            <Card className={`border-[#e1d2b3] ${activeRegistration.result === "pass" || activeRegistration.result === "merit" ? "bg-green-50" : "bg-red-50"}`}>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm font-semibold text-[#182033]">
                  <Award className="h-4 w-4" />
                  Result Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#6b6a66]">Result</span>
                  <span className={`font-semibold uppercase ${activeRegistration.result === "pass" || activeRegistration.result === "merit" ? "text-green-700" : "text-red-700"}`}>
                    {activeRegistration.result}
                  </span>
                </div>
                {activeRegistration.score && (
                  <div className="flex justify-between">
                    <span className="text-[#6b6a66]">Score</span>
                    <span className="font-medium text-[#182033]">{activeRegistration.score}%</span>
                  </div>
                )}
                {activeRegistration.examiner_remarks && (
                  <div className="pt-2 border-t border-[#e1d2b3]">
                    <p className="text-[#6b6a66] text-xs mb-1">Examiner Remarks:</p>
                    <p className="text-[#182033] italic">&ldquo;{activeRegistration.examiner_remarks}&rdquo;</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Payment Card */}
          <Card className="border-[#e1d2b3] bg-[#fdf8ef]">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm font-semibold text-[#182033]">
                <CreditCard className="h-4 w-4" />
                Payment Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[#6b6a66]">Status</span>
                <span className={`font-medium ${activeRegistration.payment_status === "paid" ? "text-green-700" : "text-amber-700"}`}>
                  {activeRegistration.payment_status === "paid" ? "Paid" : "Pending"}
                </span>
              </div>
              {activeRegistration.payment_amount && (
                <div className="flex justify-between">
                  <span className="text-[#6b6a66]">Amount</span>
                  <span className="font-medium text-[#182033]">₹{activeRegistration.payment_amount}</span>
                </div>
              )}
              {activeRegistration.payment_date && (
                <div className="flex justify-between">
                  <span className="text-[#6b6a66]">Date</span>
                  <span className="font-medium text-[#182033]">
                    {new Date(activeRegistration.payment_date).toLocaleDateString()}
                  </span>
                </div>
              )}
              <Button variant="outline" size="sm" className="w-full mt-2 border-[#d5c39d] text-xs">
                <Download className="mr-1 h-3 w-3" />
                Download Invoice
              </Button>
            </CardContent>
          </Card>

          {/* Certificate Card (if passed) */}
          {activeRegistration.status === "result_declared" && 
           (activeRegistration.result === "pass" || activeRegistration.result === "merit") && (
            <Card className="border-[#c9a227] bg-gradient-to-br from-[#fffdf5] to-[#f9f2e6]">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm font-semibold text-[#182033]">
                  <Award className="h-4 w-4 text-[#c9a227]" />
                  Certificate
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="aspect-[1.4/1] rounded-lg border-2 border-dashed border-[#c9a227] bg-white/50 flex items-center justify-center">
                  <div className="text-center p-4">
                    <Award className="h-12 w-12 mx-auto text-[#c9a227] mb-2" />
                    <p className="text-xs text-[#6b6a66]">Certificate Preview</p>
                    <p className="text-sm font-semibold text-[#182033]">{activeRegistration.instrument}</p>
                    <p className="text-xs text-[#8c7648]">Level {activeRegistration.level}</p>
                  </div>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-[#6b6a66]">Certificate ID</span>
                  <span className="font-mono text-[#182033]">
                    {activeRegistration.certificate_id || `CERT-${activeRegistration.registration_id?.slice(-8).toUpperCase()}`}
                  </span>
                </div>
                <Button className="w-full bg-[#1b2236] text-[#f8f2e6] hover:bg-[#151b2b]">
                  <Download className="mr-2 h-4 w-4" />
                  Download Certificate (PDF)
                </Button>
              </CardContent>
            </Card>
          )}
        </section>
      )}

      {/* Syllabus & Support Section */}
      <section className="grid gap-4 md:grid-cols-2">
        <Card className="border-[#e1d2b3] bg-[#fdf8ef]">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold text-[#182033]">
              <FileText className="h-4 w-4" />
              Syllabus & Exam Rules
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Link to="/instruments-levels" state={{ fromDashboard: true }} className="flex items-center justify-between text-sm text-[#182033] hover:text-[#8c7648] py-2 border-b border-[#e1d2b3]">
              <span>View Syllabus</span>
              <ExternalLink className="h-4 w-4" />
            </Link>
            <Link to="/assessment-criteria" state={{ fromDashboard: true }} className="flex items-center justify-between text-sm text-[#182033] hover:text-[#8c7648] py-2 border-b border-[#e1d2b3]">
              <span>Exam Guidelines</span>
              <ExternalLink className="h-4 w-4" />
            </Link>
            <a href="#" className="flex items-center justify-between text-sm text-[#182033] hover:text-[#8c7648] py-2">
              <span>Recording Instructions</span>
              <ExternalLink className="h-4 w-4" />
            </a>
          </CardContent>
        </Card>

        <Card className="border-[#e1d2b3] bg-[#fdf8ef]">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold text-[#182033]">
              <HelpCircle className="h-4 w-4" />
              Support
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/contact-support" state={{ fromDashboard: true }}>
              <Button variant="outline" className="w-full border-[#d5c39d] text-[#182033] hover:bg-[#f5ebd9]">
                Contact Support
              </Button>
            </Link>
            <Link to="/faqs" state={{ fromDashboard: true }} className="flex items-center justify-between text-sm text-[#182033] hover:text-[#8c7648] py-2">
              <span>Frequently Asked Questions</span>
              <ExternalLink className="h-4 w-4" />
            </Link>
          </CardContent>
        </Card>
      </section>

      {/* Academic Scholarship Section */}
      <section data-testid="scholarship-section">
        <Card className="border-[#e1d2b3] bg-gradient-to-br from-[#182033] to-[#1e3a5f] text-white overflow-hidden">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-500/20">
                <GraduationCap className="h-5 w-5 text-amber-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-white">Academic Scholarship Program</h3>
                <p className="text-xs text-slate-300 mt-1 leading-relaxed">
                  Apply for the GMC Academic Scholarship to support your tuition fees. The scholarship amount is disbursed directly to your institution/college.
                </p>
              </div>
            </div>

            {scholarshipLoading ? (
              <div className="flex items-center gap-2 text-xs text-slate-300"><Loader2 className="h-3 w-3 animate-spin" /> Loading...</div>
            ) : scholarshipStatus === "paid" ? (
              <div className="rounded-lg bg-green-600/20 border border-green-500/30 p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-400" />
                  <span className="text-sm font-medium text-green-300">Application Complete - Fee Paid</span>
                </div>
                <p className="text-xs text-slate-300">Application ID: <span className="font-mono font-semibold text-white">{scholarshipAppId}</span></p>
                <p className="text-xs text-slate-300 leading-relaxed">
                  Your scholarship application is complete. The scholarship amount will be disbursed to your institution/college within 90-120 days. You will receive an email requesting bank account details when it is ready.
                </p>
              </div>
            ) : scholarshipStatus === "submitted" ? (
              <div className="space-y-4">
                <div className="rounded-lg bg-green-600/20 border border-green-500/30 p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    <span className="text-sm font-medium text-green-300">Application Submitted</span>
                  </div>
                  <p className="text-xs text-slate-300">Application ID: <span className="font-mono font-semibold text-white">{scholarshipAppId}</span></p>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    We will notify you via email within two weeks whether you are selected for the scholarship.
                  </p>
                </div>

                <div className="rounded-lg bg-white/10 border border-white/10 p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <Button
                      data-testid="pay-scholarship-fee-btn"
                      onClick={handleScholarshipPayment}
                      disabled={scholarshipPaying}
                      className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-6 py-5"
                    >
                      <CreditCard className="h-4 w-4 mr-2" />
                      {scholarshipPaying ? "Processing..." : "Pay Application Fees (EUR 100)"}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    If you have been <strong className="text-white">selected</strong> for the scholarship and have received the scholarship decision email, you can pay the <strong className="text-amber-300">EUR 100 application fee</strong> from here to complete the process within <strong className="text-white">10 days</strong> of the decision email.
                  </p>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    If you are <strong className="text-white">not selected</strong>, there is no need to pay the application fee.
                  </p>
                </div>

                <div className="rounded-lg bg-amber-500/15 border border-amber-400/30 p-3 text-xs text-amber-200 leading-relaxed">
                  No need to pay the application fees as of now. You can pay after results are communicated to you by email. If your application fee is not paid within 10 days of the decision email, the application becomes void and you can reapply next quarter.
                </div>
              </div>
            ) : (
              <>
                <div className="rounded-lg bg-white/10 border border-white/10 p-3 space-y-2">
                  <h4 className="text-xs font-semibold text-amber-300">How It Works:</h4>
                  <ul className="text-xs text-slate-300 space-y-1.5 leading-relaxed">
                    <li className="flex items-start gap-2"><ChevronRight className="h-3 w-3 text-amber-400 mt-0.5 shrink-0" /><span>Fill the scholarship application form with your details, academics, and music background.</span></li>
                    <li className="flex items-start gap-2"><ChevronRight className="h-3 w-3 text-amber-400 mt-0.5 shrink-0" /><span>Upload all required documents and submit your application.</span></li>
                    <li className="flex items-start gap-2"><ChevronRight className="h-3 w-3 text-amber-400 mt-0.5 shrink-0" /><span>We review and notify you within <strong className="text-white">two weeks</strong> via email.</span></li>
                    <li className="flex items-start gap-2"><ChevronRight className="h-3 w-3 text-amber-400 mt-0.5 shrink-0" /><span>If selected, log in and pay the <strong className="text-white">EUR 100</strong> application fee from this dashboard within <strong className="text-white">10 days</strong>.</span></li>
                    <li className="flex items-start gap-2"><ChevronRight className="h-3 w-3 text-amber-400 mt-0.5 shrink-0" /><span>If not selected, there is <strong className="text-white">no need to pay</strong> the application fee.</span></li>
                    <li className="flex items-start gap-2"><ChevronRight className="h-3 w-3 text-amber-400 mt-0.5 shrink-0" /><span>Scholarship disbursement: <strong className="text-white">90-120 days</strong> to your institution/college.</span></li>
                    <li className="flex items-start gap-2"><ChevronRight className="h-3 w-3 text-amber-400 mt-0.5 shrink-0" /><span>If fee not paid within 10 days, the application becomes <strong className="text-white">void</strong> (reapply next quarter).</span></li>
                  </ul>
                </div>
                <div className="rounded-lg bg-amber-500/15 border border-amber-400/30 p-3 text-xs text-amber-200">
                  You can save your progress and come back anytime to complete the form.
                </div>
                <Button
                  data-testid="apply-scholarship-btn"
                  onClick={() => navigate("/student-scholarship")}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold py-5"
                >
                  <GraduationCap className="h-4 w-4 mr-2" />
                  {scholarshipStatus === "draft" ? "Continue Scholarship Application" : "Apply for Academic Scholarship"}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </section>

      {/* All Registrations History */}
      {registrations.length > 1 && (
        <section>
          <h3 className="text-sm font-semibold text-[#182033] mb-3">Exam History</h3>
          <div className="space-y-2">
            {registrations.map((reg) => (
              <div 
                key={reg.registration_id}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  reg.registration_id === activeRegistration?.registration_id 
                    ? "border-[#c9a227] bg-[#fffdf5]" 
                    : "border-[#e1d2b3] bg-white"
                }`}
              >
                <div>
                  <p className="text-sm font-medium text-[#182033]">{reg.instrument} - Level {reg.level}</p>
                  <p className="text-xs text-[#6b6a66]">
                    Registered: {new Date(reg.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusBadge(reg.status)}
                  {reg.result && (
                    <span className={`text-xs font-semibold uppercase ${
                      reg.result === "pass" || reg.result === "merit" ? "text-green-700" : "text-red-700"
                    }`}>
                      {reg.result}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Register Modal */}
      {showRegisterModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto py-4">
          <Card className="w-full max-w-lg mx-4 border-[#e1d2b3] bg-[#fdf8ef]">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#182033]">
                {registrationStep === 1 ? "Register for Exam" : registrationStep === 2 ? "Select Exam Date & Time" : "Payment"}
              </CardTitle>
              <div className="flex items-center gap-2 mt-2">
                {[1, 2, 3].map((step) => (
                  <div key={step} className="flex items-center">
                    <div className={`h-2 w-2 rounded-full ${registrationStep >= step ? 'bg-[#c9a227]' : 'bg-[#e1d2b3]'}`} />
                    {step < 3 && <div className={`h-0.5 w-8 ${registrationStep > step ? 'bg-[#c9a227]' : 'bg-[#e1d2b3]'}`} />}
                  </div>
                ))}
                <span className="text-xs text-[#6b6a66] ml-2">Step {registrationStep} of 3</span>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {registrationStep === 1 ? (
                <>
                  <div className="space-y-2">
                    <Label className="text-sm text-[#182033]">Select Instrument</Label>
                    <Select value={selectedInstrument} onValueChange={(value) => {
                      setSelectedInstrument(value);
                      setSelectedLevel("");
                    }}>
                      <SelectTrigger className="border-[#d5c39d] bg-white">
                        <SelectValue placeholder="Choose an instrument" />
                      </SelectTrigger>
                      <SelectContent>
                        {INSTRUMENTS.map((inst) => (
                          <SelectItem key={inst} value={inst}>{inst}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedInstrument && (
                    <div className="space-y-2">
                      <Label className="text-sm text-[#182033]">Intended Examination Level</Label>
                      <Select value={selectedLevel} onValueChange={setSelectedLevel}>
                        <SelectTrigger className="border-[#d5c39d] bg-white">
                          <SelectValue placeholder="Choose examination level" />
                        </SelectTrigger>
                        <SelectContent>
                          {EXAM_LEVELS.map((level) => (
                            <SelectItem key={level.value} value={level.value}>
                              {level.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-[#6b6a66]">
                        Select your intended examination level or certification.
                      </p>
                    </div>
                  )}

                  {selectedInstrument && selectedLevel && (
                    <div className="rounded-lg border border-[#c9a227] bg-[#fffdf5] p-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-[#6b6a66]">Exam Fee</span>
                        <span className="text-lg font-bold text-[#182033]">{formatPrice(getExamFee())}</span>
                      </div>
                      <p className="text-xs text-[#8c7648] mt-1">
                        {selectedLevel === "ai-voiceover" || selectedLevel === "music-therapy" ? "Professional certification" : "Per level certification"}
                      </p>
                    </div>
                  )}

                  <div className="flex gap-2 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowRegisterModal(false);
                        setRegistrationStep(1);
                        setSelectedInstrument("");
                        setSelectedLevel("");
                        setSelectedDate(null);
                        setSelectedTime("");
                      }}
                      className="flex-1 border-[#d5c39d]"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => setRegistrationStep(2)}
                      disabled={!selectedInstrument || !selectedLevel}
                      className="flex-1 bg-[#1b2236] text-[#f8f2e6] hover:bg-[#151b2b]"
                    >
                      Select Date & Time
                    </Button>
                  </div>
                </>
              ) : registrationStep === 2 ? (
                <>
                  {/* Date & Time Selection Step */}
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-sm text-[#182033] flex items-center gap-2">
                        <CalendarIcon className="h-4 w-4" />
                        Select Exam Date
                      </Label>
                      <p className="text-xs text-[#6b6a66]">
                        Dates within the next 2 weeks are not available for booking.
                      </p>
                      <div className="flex justify-center border border-[#d5c39d] rounded-lg bg-white p-2">
                        <Calendar
                          mode="single"
                          selected={selectedDate}
                          onSelect={setSelectedDate}
                          disabled={isDateDisabled}
                          fromDate={minSelectableDate}
                          className="rounded-md"
                        />
                      </div>
                      {selectedDate && (
                        <p className="text-sm text-[#182033] text-center">
                          Selected: <span className="font-semibold">{selectedDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm text-[#182033] flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        Select Exam Time
                      </Label>
                      <p className="text-xs text-[#6b6a66]">
                        Time shown in your local timezone ({Intl.DateTimeFormat().resolvedOptions().timeZone})
                      </p>
                      <Select value={selectedTime} onValueChange={setSelectedTime}>
                        <SelectTrigger className="border-[#d5c39d] bg-white">
                          <SelectValue placeholder="Choose a time slot" />
                        </SelectTrigger>
                        <SelectContent>
                          {TIME_SLOTS.map((slot) => (
                            <SelectItem key={slot.value} value={slot.value}>
                              {slot.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => setRegistrationStep(1)}
                      className="flex-1 border-[#d5c39d]"
                    >
                      Back
                    </Button>
                    <Button
                      onClick={() => setRegistrationStep(3)}
                      disabled={!selectedDate || !selectedTime}
                      className="flex-1 bg-[#1b2236] text-[#f8f2e6] hover:bg-[#151b2b]"
                    >
                      Proceed to Payment
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  {/* Payment Step */}
                  <div className="rounded-lg border border-[#e1d2b3] bg-white p-4 space-y-3">
                    <h4 className="text-sm font-semibold text-[#182033]">Order Summary</h4>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#6b6a66]">Instrument</span>
                      <span className="font-medium text-[#182033]">{selectedInstrument}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#6b6a66]">Level</span>
                      <span className="font-medium text-[#182033]">
                        {EXAM_LEVELS.find(l => l.value === selectedLevel)?.label || `Level ${selectedLevel}`}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#6b6a66]">Exam Date</span>
                      <span className="font-medium text-[#182033]">
                        {selectedDate?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#6b6a66]">Exam Time</span>
                      <span className="font-medium text-[#182033]">
                        {TIME_SLOTS.find(t => t.value === selectedTime)?.label}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#6b6a66]">Exam Type</span>
                      <span className="font-medium text-[#182033]">Live Online Examination</span>
                    </div>
                    <div className="border-t border-[#e1d2b3] pt-2 space-y-1">
                      <div className="flex justify-between">
                        <span className="text-sm font-semibold text-[#182033]">Total (EUR)</span>
                        <span className="text-lg font-bold text-[#8c7648]">{formatPrice(getExamFee())}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-[#6b6a66]">Amount in INR (approx.)</span>
                        <span className="text-sm font-semibold text-[#182033]">₹{getINRAmount(getExamFee()).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-blue-300 bg-blue-50 p-3">
                    <p className="text-xs text-blue-800">
                      <strong>Secure Payment via Razorpay</strong><br />
                      Click the button below to pay securely using UPI, Credit/Debit Card, Net Banking, or Wallets.
                    </p>
                  </div>

                  <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                    <p className="text-xs text-red-700">
                      <strong>Important:</strong> Exam fees are non-refundable and non-transferable. 
                      Rescheduling is allowed only once, up to 72 hours before the exam.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Button
                      onClick={handleRazorpayPayment}
                      disabled={registering}
                      className="w-full bg-[#2563eb] text-white hover:bg-[#1d4ed8]"
                    >
                      <CreditCard className="mr-2 h-4 w-4" />
                      {registering ? "Processing..." : `Pay ₹${getINRAmount(getExamFee()).toLocaleString()} with Razorpay`}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setRegistrationStep(2)}
                      disabled={registering}
                      className="w-full border-[#d5c39d]"
                    >
                      Back
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Reschedule Modal */}
      {showRescheduleModal && activeRegistration && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto py-4">
          <Card className="w-full max-w-lg mx-4 border-[#e1d2b3] bg-[#fdf8ef]">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#182033] flex items-center gap-2">
                <CalendarIcon className="h-5 w-5" />
                Reschedule Exam
              </CardTitle>
              <p className="text-xs text-[#6b6a66]">
                Select a new date and time for your examination
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Current Exam Info */}
              <div className="rounded-lg bg-slate-100 p-3">
                <p className="text-xs text-[#6b6a66] mb-1">Current Exam Schedule:</p>
                <p className="text-sm font-medium text-[#182033]">
                  {activeRegistration.instrument} - Level {activeRegistration.level}
                </p>
                <p className="text-sm text-[#8c7648]">
                  {new Date(activeRegistration.exam_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} at {(() => {
                    const hour = parseInt(activeRegistration.exam_time.split(':')[0], 10);
                    const period = hour >= 12 ? 'PM' : 'AM';
                    const displayHour = hour > 12 ? hour - 12 : hour;
                    const timezone = activeRegistration.student_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
                    return `${displayHour}:00 ${period} (${timezone})`;
                  })()}
                </p>
              </div>

              {/* Warning Notice */}
              <div className="rounded-lg bg-amber-50 border border-amber-300 p-3">
                <p className="text-xs text-amber-800">
                  <strong>⚠️ Important:</strong> Exam rescheduling is allowed <strong>only once</strong>. 
                  After rescheduling, you will not be able to change the date again. 
                  Exam fees are non-refundable and non-transferable.
                </p>
              </div>

              {/* New Date Selection */}
              <div className="space-y-2">
                <Label className="text-sm text-[#182033] flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  Select New Exam Date
                </Label>
                <p className="text-xs text-[#6b6a66]">
                  Dates within the next 2 weeks are not available for booking.
                </p>
                <div className="flex justify-center border border-[#d5c39d] rounded-lg bg-white p-2">
                  <Calendar
                    mode="single"
                    selected={rescheduleDate}
                    onSelect={setRescheduleDate}
                    disabled={isDateDisabled}
                    fromDate={minSelectableDate}
                    className="rounded-md"
                  />
                </div>
                {rescheduleDate && (
                  <p className="text-sm text-[#182033] text-center">
                    New Date: <span className="font-semibold">{rescheduleDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                  </p>
                )}
              </div>

              {/* New Time Selection */}
              <div className="space-y-2">
                <Label className="text-sm text-[#182033] flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Select New Exam Time
                </Label>
                <p className="text-xs text-[#6b6a66]">
                  Time shown in your local timezone ({Intl.DateTimeFormat().resolvedOptions().timeZone})
                </p>
                <Select value={rescheduleTime} onValueChange={setRescheduleTime}>
                  <SelectTrigger className="border-[#d5c39d] bg-white">
                    <SelectValue placeholder="Choose a time slot" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map((slot) => (
                      <SelectItem key={slot.value} value={slot.value}>
                        {slot.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowRescheduleModal(false);
                    setRescheduleDate(null);
                    setRescheduleTime("");
                  }}
                  className="flex-1 border-[#d5c39d]"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleRescheduleExam}
                  disabled={!rescheduleDate || !rescheduleTime || rescheduling}
                  className="flex-1 bg-[#1b2236] text-[#f8f2e6] hover:bg-[#151b2b]"
                >
                  {rescheduling ? "Rescheduling..." : "Confirm Reschedule"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
